import { useState } from "react";
import { formatTimestamp } from "../../lib/format";
import {
  api,
  formatMs,
  formatPct,
  type ProviderDefinitionDto,
  type ProviderVerificationDto,
  type ProviderView,
} from "../../lib/api";
import {
  capChips,
  credentialsLabel,
  freeBadge,
  isNoKey,
  quotaDetail,
  quotaLabel,
  requirementLabel,
} from "../../lib/provider-display";
import {
  ProviderMark,
  Drawer,
  DrawerSection,
  StatusDot,
  VerificationChecklist,
} from "../../components/ui";

/* ── Provider detail drawer (connected) ────────────────────────── */

export function ProviderDetailDrawer({
  provider,
  onClose,
  onChanged,
}: {
  provider: ProviderView;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const p = provider;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [newKey, setNewKey] = useState("");
  const [addingKey, setAddingKey] = useState(false);

  const keyMode = p.definition.freeAccess?.keyRequired;
  const canAddKey = p.kind !== "demo" && keyMode !== "none";
  const isAnonymous = p.connectionMode === "anonymous";

  async function run(fn: () => Promise<void>, okMsg?: string) {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      if (okMsg) setMsg(okMsg);
      await onChanged();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  const perf: Array<[string, string]> = [
    ["Latency (p50)", formatMs(p.health?.p50LatencyMs)],
    ["Success rate", formatPct(p.health?.successRate)],
    ["Last verified", formatTimestamp(p.lastVerifiedAt)],
    ["Routing priority", p.priority != null ? `#${p.priority}` : "—"],
  ];

  return (
    <Drawer
      title={
        <span className="flex items-center gap-3">
          <ProviderMark letter={p.definition.branding.letter} accent={p.definition.branding.accent} size={32} />
          <span>
            {p.name}
            <span className="ml-2">
              <StatusDot status={p.connectionStatus} />
            </span>
          </span>
        </span>
      }
      onClose={onClose}
      wide
    >
      <p className="mb-5 text-sm text-canvas-muted">{p.shortDescription || p.description}</p>

      {/* ACCESS */}
      <DrawerSection title="Access">
        <div className="rounded-xl border border-canvas-border bg-canvas p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{credentialsLabel(p)}</span>
            {canAddKey && (
              <button
                className="btn-ghost !px-2.5 !py-1 text-xs"
                onClick={() => setAddingKey((v) => !v)}
              >
                {addingKey ? "Cancel" : "+ Add API Key"}
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-canvas-muted">
            {isAnonymous
              ? "Anonymous access — no API key required. Add a key for higher limits."
              : p.credentials?.state === "missing"
                ? "This provider cannot route requests until a key is added."
                : p.credentials?.detail ?? "Credential configured."}
          </p>
          {p.credentials?.state === "missing" && (
            <p className="mt-2 rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-xs text-danger">
              API key required.
            </p>
          )}
          {p.keyLabels.length > 0 && (
            <ul className="mt-2.5 space-y-1">
              {p.keyLabels.map((k) => (
                <li
                  key={k.id}
                  className="flex items-center justify-between rounded-lg border border-canvas-border px-3 py-1.5 text-xs"
                >
                  <span>
                    {k.label} <span className="mono text-canvas-muted">{k.prefix}</span>
                    {k.isPrimary && <span className="ml-1 text-accent">· primary</span>}
                  </span>
                  <span className="text-canvas-muted">{k.status}</span>
                </li>
              ))}
            </ul>
          )}
          {addingKey && (
            <div className="mt-2.5 flex gap-2">
              <input
                type="password"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder={keyMode === "optional" ? "Optional API key (higher limits)" : "Paste API key"}
                className="flex-1 rounded-lg border border-canvas-border bg-canvas-raised px-3 py-2 font-mono text-sm outline-none focus:border-accent"
                autoComplete="off"
              />
              <button
                className="btn-primary"
                disabled={busy || newKey.trim().length < 8}
                onClick={() =>
                  void run(async () => {
                    await api(`/api/providers/${p.id}/keys`, {
                      method: "POST",
                      body: JSON.stringify({ apiKey: newKey.trim() }),
                    });
                    await api(`/api/providers/${p.id}/verify`, { method: "POST" });
                    setNewKey("");
                    setAddingKey(false);
                  }, "Key added and verified")
                }
              >
                Add
              </button>
            </div>
          )}
        </div>
      </DrawerSection>

      {/* CAPABILITIES */}
      <DrawerSection title="Capabilities">
        <div className="flex flex-wrap gap-1.5">
          {capChips(p.definition.capabilities, 10).map((c) => (
            <span key={c} className="chip-muted">{c}</span>
          ))}
          {capChips(p.definition.capabilities, 10).length === 0 && (
            <span className="text-xs text-canvas-muted">Unknown</span>
          )}
        </div>
      </DrawerSection>

      {/* MODELS */}
      <DrawerSection title="Models">
        <div className="flex items-center justify-between rounded-xl border border-canvas-border bg-canvas px-3.5 py-2.5">
          <span className="text-sm">
            {p.modelCount} models
            {p.freeModelCount > 0 && (
              <span className="ml-2 text-accent">{p.freeModelCount} free</span>
            )}
          </span>
          <a className="btn-ghost !px-2.5 !py-1 text-xs" href="/models">
            View models →
          </a>
        </div>
      </DrawerSection>

      {/* PERFORMANCE */}
      <DrawerSection title="Performance">
        <dl className="grid grid-cols-2 gap-2">
          {perf.map(([k, v]) => (
            <div key={k} className="rounded-lg border border-canvas-border bg-canvas px-3 py-2">
              <dt className="text-[10px] uppercase tracking-wider text-canvas-muted">{k}</dt>
              <dd className="mt-0.5 text-sm font-medium">{v}</dd>
            </div>
          ))}
        </dl>
      </DrawerSection>

      {/* QUOTA */}
      <DrawerSection title="Quota">
        <div className="rounded-xl border border-canvas-border bg-canvas p-3.5">
          <span className="text-sm font-medium">{quotaLabel(p)}</span>
          {quotaDetail(p).length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-wider text-canvas-muted hover:text-canvas-ink">
                Details
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-canvas-muted">
                {quotaDetail(p).map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </DrawerSection>

      {/* VERIFICATION */}
      {p.verification && (
        <DrawerSection title="Last verification">
          <VerificationChecklist verification={p.verification} />
        </DrawerSection>
      )}

      {msg && <p className="mb-3 text-sm text-accent">{msg}</p>}

      {/* ACTIONS */}
      <div className="flex flex-wrap gap-2">
        <button
          className="btn-primary"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await api(`/api/providers/${p.id}/verify`, { method: "POST" });
            }, "Verification completed")
          }
        >
          {busy ? "Working…" : "Test connection"}
        </button>
        <button
          className="btn-ghost"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await api(`/api/providers/${p.id}`, {
                method: "PATCH",
                body: JSON.stringify({
                  status: p.connectionStatus === "disabled" ? "active" : "disabled",
                }),
              });
            })
          }
        >
          {p.connectionStatus === "disabled" ? "Enable" : "Disable"}
        </button>
        {p.definition.docsUrl && (
          <a className="btn-ghost" href={p.definition.docsUrl} target="_blank" rel="noreferrer">
            Docs ↗
          </a>
        )}
        <button
          className="btn-ghost text-danger hover:border-danger/50"
          disabled={busy}
          onClick={() => {
            if (!confirm("Remove credentials for this provider?")) return;
            void run(async () => {
              await api(`/api/providers/${p.id}/credentials`, { method: "DELETE" });
            });
          }}
        >
          Disconnect
        </button>
      </div>
    </Drawer>
  );
}

/* ── Connect drawer: managed (stepped) ─────────────────────────── */

export function ManagedConnect({
  def,
  onDone,
}: {
  def: ProviderDefinitionDto;
  onDone: () => Promise<void>;
}) {
  const noKey = isNoKey(def);
  const optional = def.freeAccess?.keyRequired === "optional";
  const [apiKey, setApiKey] = useState("");
  const [extras, setExtras] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [verification, setVerification] = useState<ProviderVerificationDto | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const extraFields = def.credentialFields.filter(
    (f) => f.key !== "apiKey" && f.key !== "apiToken",
  );
  const missingRequiredExtra = extraFields.some(
    (f) => f.required && !(extras[f.key] ?? "").trim(),
  );
  const needsKey = !noKey;
  const canSubmit =
    !busy &&
    (noKey || apiKey.trim().length >= 8) &&
    !(needsKey && missingRequiredExtra);

  async function connect() {
    setBusy(true);
    setFormError(null);
    setVerification(null);
    try {
      const config: Record<string, string> = {};
      for (const f of extraFields) {
        const v = (extras[f.key] ?? "").trim();
        if (v) config[f.key] = v;
      }
      const res = await api<{ verification: ProviderVerificationDto }>(
        "/api/providers/connect",
        {
          method: "POST",
          body: JSON.stringify({
            providerTypeId: def.id,
            apiKey: apiKey.trim() || undefined,
            config,
          }),
        },
      );
      setVerification(res.verification);
      if (res.verification.ok) {
        await new Promise((r) => setTimeout(r, 700));
        await onDone();
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Connect failed");
    } finally {
      setBusy(false);
    }
  }

  const badge = freeBadge(def);
  let step = 0;

  return (
    <div>
      <div className="mb-5 flex items-start gap-3">
        <ProviderMark letter={def.branding.letter} accent={def.branding.accent} size={42} />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{def.name}</h3>
            {badge && <span className="chip-ok !text-[10px]">{badge}</span>}
          </div>
          <p className="mt-0.5 text-xs text-canvas-muted">{def.description}</p>
          <p className="mt-1 text-[11px] text-canvas-muted">{requirementLabel(def)}</p>
        </div>
      </div>

      {noKey && (
        <div className="mb-4 rounded-xl border border-accent/30 bg-accent/5 px-3.5 py-3">
          <p className="text-sm font-medium text-accent">
            {optional ? "Anonymous access supported" : "No API key required"}
          </p>
          <p className="mt-0.5 text-xs text-canvas-muted">
            {optional
              ? "Connect without a key for free anonymous access — add a key below for higher limits."
              : "This source works without credentials."}
          </p>
        </div>
      )}

      {/* Step 1 — get a key (required-key providers) */}
      {needsKey && def.apiKeyUrl && (
        <Step n={++step} title="Get your API key" done={false}>
          <p className="mb-2 text-xs text-canvas-muted">
            {def.setupInstructions.description || def.setupInstructions.title}
          </p>
          {def.setupInstructions.steps.length > 0 && (
            <ol className="mb-3 space-y-1 text-xs text-canvas-muted">
              {def.setupInstructions.steps.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mono text-accent">{i + 1}.</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          )}
          <a className="btn-primary !py-1.5 text-xs" href={def.apiKeyUrl} target="_blank" rel="noreferrer">
            Open {def.name} ↗
          </a>
        </Step>
      )}

      {/* Step 2 — credentials (hidden only for strict no-key) */}
      {(needsKey || optional) && (
        <Step n={++step} title={optional ? "Paste your key (optional)" : "Paste your key"} done={false}>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={def.credentialFields[0]?.placeholder ?? "Paste your API key"}
            className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 font-mono text-sm outline-none focus:border-accent"
            autoFocus
            autoComplete="off"
          />
          <p className="mt-1.5 text-[11px] text-canvas-muted">
            Stored encrypted — the endpoint is already configured.
          </p>
          {extraFields.map((f) => (
            <div key={f.key} className="mt-3">
              <label className="mb-1 block text-[11px] font-medium text-canvas-muted">
                {f.label}
                {f.required ? " *" : ""}
              </label>
              <input
                value={extras[f.key] ?? ""}
                onChange={(e) => setExtras((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 font-mono text-sm outline-none focus:border-accent"
              />
              {f.helpText && <p className="mt-1 text-[11px] text-canvas-muted">{f.helpText}</p>}
            </div>
          ))}
        </Step>
      )}

      {/* Final step — verify */}
      <Step n={++step} title="Verify" done={Boolean(verification?.ok)}>
        {formError && (
          <p className="mb-2 rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger">
            {formError}
          </p>
        )}
        {verification && <VerificationChecklist verification={verification} />}
        <button
          className="btn-primary w-full justify-center"
          disabled={!canSubmit}
          onClick={() => void connect()}
        >
          {busy
            ? "Connecting & verifying…"
            : verification?.ok
              ? "Connected ✓"
              : noKey && !apiKey.trim()
                ? "Connect without key"
                : "Connect & Verify"}
        </button>
      </Step>
    </div>
  );
}

function Step({
  n,
  title,
  done,
  children,
}: {
  n: number;
  title: string;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4 rounded-xl border border-canvas-border bg-canvas p-3.5">
      <div className="mb-2.5 flex items-center gap-2">
        <span
          className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${
            done ? "bg-accent text-canvas" : "bg-canvas-raised text-canvas-muted"
          }`}
        >
          {done ? "✓" : n}
        </span>
        <span className="text-xs font-semibold uppercase tracking-wider text-canvas-ink">
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

/* ── Connect drawer: custom ────────────────────────────────────── */

export function CustomConnect({ onDone }: { onDone: () => Promise<void> }) {
  const [baseUrl, setBaseUrl] = useState("http://localhost:11434/v1/");
  const [apiKey, setApiKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [modelIds, setModelIds] = useState("");
  const [busy, setBusy] = useState(false);
  const [verification, setVerification] = useState<ProviderVerificationDto | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  async function connect() {
    setBusy(true);
    setFormError(null);
    setVerification(null);
    try {
      const res = await api<{ verification: ProviderVerificationDto }>(
        "/api/providers/custom",
        {
          method: "POST",
          body: JSON.stringify({
            baseUrl: baseUrl.trim(),
            apiKey: apiKey.trim() || undefined,
            displayName: displayName.trim() || undefined,
            modelIds: modelIds.split(",").map((s) => s.trim()).filter(Boolean),
          }),
        },
      );
      setVerification(res.verification);
      if (res.verification.ok) {
        await new Promise((r) => setTimeout(r, 400));
        await onDone();
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Connect failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p className="mb-4 text-sm text-canvas-muted">
        Any OpenAI-compatible endpoint. This is the only flow that needs a base URL.
      </p>
      <Field label="Base URL *">
        <input
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://example.com/v1"
          className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 font-mono text-sm outline-none focus:border-accent"
        />
      </Field>
      <Field label="API key (optional)">
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 font-mono text-sm outline-none focus:border-accent"
        />
      </Field>
      <Field label="Model IDs (comma-separated)">
        <input
          value={modelIds}
          onChange={(e) => setModelIds(e.target.value)}
          placeholder="llama3, mixtral"
          className="w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 font-mono text-sm outline-none focus:border-accent"
        />
      </Field>
      <Field label="Display name (optional)">
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="mb-4 w-full rounded-lg border border-canvas-border bg-canvas px-3 py-2 text-sm outline-none focus:border-accent"
        />
      </Field>
      {formError && (
        <p className="mb-3 rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger">
          {formError}
        </p>
      )}
      {verification && <VerificationChecklist verification={verification} />}
      <button
        className="btn-primary w-full justify-center"
        disabled={busy || !baseUrl.trim()}
        onClick={() => void connect()}
      >
        {busy ? "Connecting…" : "Test & Connect"}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
        {label}
      </label>
      {children}
    </div>
  );
}
